// Dashboard widgets and their child cards. Mirrors investor-backend admin/analytics/dashboard-layout.ts.
export const CATALOG = [
  {
    key: "key_metrics",
    label: "Key Metrics",
    items: [
      { key: "new_startups", label: "New Startups" },
      { key: "new_investors", label: "New Investors" },
      { key: "invitations_today", label: "Round Invitations" },
      { key: "subscriptions", label: "Subscriptions Purchased" },
    ],
  },
  { key: "pipeline", label: "Pipeline" },
  { key: "daily_activity", label: "Daily Activity" },
  { key: "plan_mix", label: "Plan Mix & Revenue" },
  {
    key: "assessments",
    label: "AI Insights",
    items: [
      { key: "fundability", label: "Fundability" },
      { key: "pitchdeck", label: "Pitch Deck" },
    ],
  },
  {
    key: "composition",
    label: "Composition",
    items: [
      { key: "country", label: "Top Countries" },
      { key: "stage", label: "Stage" },
      { key: "category", label: "Category & Sector" },
    ],
  },
  { key: "campaign_attribution", label: "Campaign Attribution" },
  // Startup platform widgets — startup-backend /admin/platform-analytics.
  { key: "profile_completion", label: "Profile Completion" },
  {
    key: "pitch_uploads",
    label: "Pitch Uploads",
    items: [
      { key: "pitch_deck", label: "Pitch Deck" },
      { key: "video_pitch", label: "Video Pitch" },
      { key: "product_video", label: "Product Video" },
    ],
  },
  { key: "active_users", label: "Active Users" },
  { key: "session_time", label: "Average Session Time" },
  {
    key: "platform_activity",
    label: "Activity",
    items: [
      { key: "startup_signups", label: "Founder Signups" },
      { key: "investor_signups", label: "Investor Signups" },
      { key: "startups_created", label: "Startup Profiles Created" },
      { key: "documents_uploaded", label: "Documents Uploaded" },
      { key: "investor_connections", label: "Investor–Startup Connections" },
      { key: "data_rooms_created", label: "Data Rooms Created" },
    ],
  },
  { key: "rounds", label: "Rounds Created" },
  { key: "startup_views", label: "Startup Profile Views" },
  { key: "data_rooms", label: "Data Rooms" },
];

export const WIDGET = Object.fromEntries(CATALOG.map((w) => [w.key, w]));
export const WIDGET_LABELS = Object.fromEntries(CATALOG.map((w) => [w.key, w.label]));

export const DENSITIES = [
  { key: "comfortable", label: "Comfortable", gap: 32, fullWidth: true },
  { key: "balanced", label: "Balanced", gap: 24, fullWidth: false },
  { key: "compact", label: "Compact", gap: 12, fullWidth: false },
];

export const PRESETS = [
  { key: "executive", label: "Executive Overview", description: "KPI cards first, then pipeline and activity",
    order: ["key_metrics", "pipeline", "daily_activity", "plan_mix", "assessments", "composition", "campaign_attribution", "profile_completion", "pitch_uploads", "active_users", "session_time", "platform_activity", "rounds", "startup_views", "data_rooms"] },
  { key: "revenue", label: "Revenue Focus", description: "Plan mix and assessments near the top",
    order: ["key_metrics", "plan_mix", "assessments", "campaign_attribution", "pipeline", "daily_activity", "composition", "profile_completion", "pitch_uploads", "rounds", "data_rooms", "startup_views", "platform_activity", "active_users", "session_time"] },
  { key: "composition", label: "Startup Composition", description: "Countries, stages and sectors first",
    order: ["composition", "profile_completion", "pitch_uploads", "key_metrics", "campaign_attribution", "pipeline", "daily_activity", "plan_mix", "assessments", "platform_activity", "rounds", "startup_views", "data_rooms", "active_users", "session_time"] },
  { key: "startup_engagement", label: "Startup Engagement", description: "Profile completion, activity and investor views first",
    order: ["profile_completion", "platform_activity", "active_users", "session_time", "startup_views", "pitch_uploads", "rounds", "data_rooms", "key_metrics", "pipeline", "daily_activity", "plan_mix", "assessments", "composition", "campaign_attribution"] },
];

const obj = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v : {});
const orderOf = (given, known) => {
  const out = [];
  (Array.isArray(given) ? given : []).forEach((k) => { if (known.includes(k) && !out.includes(k)) out.push(k); });
  return [...out, ...known.filter((k) => !out.includes(k))];
};

// { widgets: { [w]: { visible, items? } }, order: [w], itemOrder: { [w]: [card] }, density }
export const normalizeLayout = (input) => {
  const src = obj(input);
  const widgetsIn = src.widgets ? obj(src.widgets) : src;
  const widgets = {};
  const itemOrder = {};
  CATALOG.forEach((w) => {
    const given = obj(widgetsIn[w.key]);
    widgets[w.key] = { visible: given.visible !== false };
    if (w.items) {
      const keys = w.items.map((i) => i.key);
      widgets[w.key].items = Object.fromEntries(keys.map((k) => [k, obj(given.items)[k] !== false]));
      itemOrder[w.key] = orderOf(obj(src.itemOrder)[w.key], keys);
    }
  });
  return {
    widgets,
    order: orderOf(src.order, CATALOG.map((w) => w.key)),
    itemOrder,
    density: DENSITIES.some((d) => d.key === src.density) ? src.density : "balanced",
  };
};

export const DEFAULT_LAYOUT = normalizeLayout();

// What the dashboard asks of a layout. A widget whose every card is hidden hides too.
export const makeView = (layout) => {
  const l = layout || DEFAULT_LAYOUT;
  const density = DENSITIES.find((d) => d.key === l.density) || DENSITIES[1];
  const item = (widget, key) => l.widgets?.[widget]?.visible !== false && l.widgets?.[widget]?.items?.[key] !== false;
  const widget = (key) => {
    const w = l.widgets?.[key];
    if (w?.visible === false) return false;
    return !w?.items || Object.values(w.items).some(Boolean);
  };
  // Cards in the saved order; keys not in the catalog keep their place at the end.
  const sortItems = (widgetKey, rows) => {
    const order = l.itemOrder?.[widgetKey] || [];
    const rank = (r) => { const i = order.indexOf(r.key); return i < 0 ? order.length : i; };
    return [...(rows || [])].sort((a, b) => rank(a) - rank(b));
  };
  // Grid placement: a design's full-width widgets span both columns; Comfortable spans everything.
  // Given the widgets actually shown, in order, a half-width widget left without a partner —
  // the last one, or one followed by a full-width widget — spans the row too, so no row ever
  // ends in an empty half. Hiding or reordering widgets re-pairs the rest automatically.
  const span = (key, fullWidth, shown) => {
    if (density.fullWidth || fullWidth.includes(key)) return "1 / -1";
    if (!shown) return "auto";
    let open = null;
    const lone = new Set();
    shown.forEach((k) => {
      if (fullWidth.includes(k)) { if (open) lone.add(open); open = null; }
      else if (open) open = null;
      else open = k;
    });
    if (open) lone.add(open);
    return lone.has(key) ? "1 / -1" : "auto";
  };
  return { order: l.order || DEFAULT_LAYOUT.order, widget, item, sortItems, span, gap: density.gap, density: density.key };
};

export const ALL_VISIBLE = makeView(DEFAULT_LAYOUT);

// Hidden widgets plus hidden cards inside visible widgets, for the "N hidden" badge.
export const countHidden = (layout) =>
  CATALOG.reduce((n, w) => {
    const entry = layout?.widgets?.[w.key];
    if (entry?.visible === false) return n + 1;
    return n + Object.values(entry?.items || {}).filter((v) => v === false).length;
  }, 0);

// Moves one key up (-1) or down (+1) in a list; a no-op at either end.
export const move = (list, key, dir) => {
  const i = list.indexOf(key);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

// Moves the item at `from` to `to` (drag-and-drop); out-of-range indexes leave the list as is.
export const reorder = (list, from, to) => {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};
